import React, { useContext, useState, useRef, useEffect, useCallback } from "react";
import {
  Button,
  Paper,
  Text,
  Box,
  Stack,
  Group,
  ThemeIcon,
  LoadingOverlay,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import {
  IconFaceId,
  IconSunHigh,
  IconChefHatOff,
  IconEyeglass2,
} from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";
import { isMobile, isFirefox } from "react-device-detect";
import { DataContext } from "../../../context/DataContext";
import { uploadImageToS3 } from "../../../helpers";
import VerificationProcessingScreen from "../../../components/verification-processing-screen";
import {
  requestCaptureChallenge,
  processAndUploadWithIntegrity,
  createVisibilityTracker,
  createTouchTracker,
  createMotionTracker,
} from "../../../utils/captureIntegrity";
import FirefoxCameraWarning from "../../../components/FirefoxCameraWarning";
import AndroidCameraBlockPortal, { fireAndroidBlockRecoveredIfPending } from "../../../components/AndroidCameraBlockPortal";
import { getAndroidBlockVariant } from "../../../utils/androidDetection";
import VeratadCapture from "./capture";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import useLocale from "../../../hooks/useLocale";
import { preloadFaceDetectionModels } from "../../../hooks/useFaceDetection";
import { trackEvent } from "../../../utils/telemetry";
import { getEdgeQueueUrl } from "../../../utils/edge";
import { faceMatchV2Enabled } from "../../../utils/featureFlags";
import VeratadFaceCaptureV2 from "./index-v2";

// Module-level callback for camera denial — set at module scope so it exists
// before any React component mounts. capture.js calls this via window global.
// The setter is wired up inside the component to trigger a React state update.
let _setCameraError = null;
window.__veratadCameraError = () => {
  console.log("[FaceCapture] __veratadCameraError called, _setCameraError:", typeof _setCameraError);
  if (_setCameraError) _setCameraError(true);
};


// Which face-match capture this session gets. v1 is the static intro page
// below; v2 (./index-v2.js) is the age-estimation experience. Resolved once
// per mount and pinned, so a flag that arrives late cannot swap the screen
// under a user who is already reading or capturing.
export default function VeratadFaceCapture(props) {
  const [useV2] = useState(faceMatchV2Enabled);
  return useV2 ? <VeratadFaceCaptureV2 {...props} /> : <VeratadFaceCaptureV1 {...props} />;
}

function VeratadFaceCaptureV1({ onCompletion }) {
  const { data, settings, dispatch, postPayloadMessage } =
    useContext(DataContext);
  const [isCapture, setIsCapture] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReadyForStatusCheck, setIsReadyForStatusCheck] = useState(false);
  // Standalone-only: terminal status captured from polling so the
  // processing screen can hold "Completing verification…" before
  // firing onComplete which dispatches navigation. Embedded usage
  // (with onCompletion prop) returns before polling starts.
  const [terminalStatus, setTerminalStatus] = useState(null);
  const [cameraError, setCameraError] = useState(false);
  // The selfie upload failed and the intro is back for a retry. Journey
  // API prefill sessions upload presigned only (helpers/index.js), so a
  // failure there is not hidden by the Cognito fallback.
  const [uploadFailed, setUploadFailed] = useState(false);
  const [showFirefoxPrompt, setShowFirefoxPrompt] = useState(
    isMobile && isFirefox
  );
  const theme = useMantineTheme();
  // Same 395px breakpoint the dcams intro uses to keep the Get
  // Started button on-screen for small phones. Below this the intro
  // shrinks fonts / icons / paddings so the checklist + button all
  // fit in the visible viewport instead of pushing the button
  // out of reach.
  const isSmallMobile = useMediaQuery("(max-width: 395px)");
  const locale = useLocale("face-capture");
  const loadingStartTimeRef = useRef(null);
  // True while a capture is being uploaded/dispatched. Guards against
  // duplicate submissions — a stalled device can deliver several
  // capture callbacks (queued manual-shutter taps) for one attempt.
  const uploadInFlightRef = useRef(false);
  const challengeRef = useRef(null);
  const sessionTimestampsRef = useRef({ session_start: Date.now() });
  const telemetryRef = useRef(null);
  const captureFailedFiredRef = useRef(false);

  // Kick off the face-api model fetch (tinyFaceDetector + landmark68
  // — ~560KB total across 4 self-hosted files) the moment the intro
  // screen mounts, instead of waiting for the user to click Get
  // Started. `modelsReady` gates the Get Started button so the user
  // can't tap through to the camera while the model bytes are still
  // arriving — they wait on the intro (which is content to read)
  // instead of staring at a blank loading state inside the camera.
  // Disproportionately helps users on slow network paths (e.g.
  // mainland CN hairpinning through HK/SG via Cloudflare's
  // non-licensed edge), where the model fetch is the biggest
  // contributor to the 20–30s warm-up.
  const [modelsReady, setModelsReady] = useState(false);
  useEffect(() => {
    // Funnel top: the intro screen rendered — everything upstream of
    // the Get Started click (capture_started) anchors to this.
    trackEvent("landed", { face_ui: "match_v1" }, { once: true });
    let cancelled = false;
    preloadFaceDetectionModels()
      .then(() => {
        if (!cancelled) setModelsReady(true);
      })
      .catch(() => {
        // Hook will retry + surface the error when capture mounts;
        // unblock the button anyway so the user isn't permanently
        // stuck if the preload fails for a transient reason.
        if (!cancelled) setModelsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Wire up the module-level callback on every render so it always has
  // fresh references to setState. This runs in the render phase (not a hook),
  // so it's immune to conditional-hook ordering issues.
  _setCameraError = (val) => {
    console.log("[FaceCapture] _setCameraError called, unmounting capture and showing error");
    setLoading(false);
    setIsCapture(false);
    setCameraError(val);
    if (telemetryRef.current) {
      telemetryRef.current.visibility?.destroy?.();
      telemetryRef.current.touch?.destroy?.();
      telemetryRef.current.motion?.destroy?.();
      telemetryRef.current = null;
    }
  };

  // Listen for camera error DOM events from capture.js — this is the most
  // reliable communication channel, immune to code-splitting module issues
  // and stale closure problems that break the global callback and onError prop.
  useEffect(() => {
    const handleCameraErrorEvent = () => {
      console.log("[FaceCapture] veratad-camera-error DOM event received");
      setLoading(false);
      setIsCapture(false);
      setCameraError(true);
      if (telemetryRef.current) {
        telemetryRef.current.visibility?.destroy?.();
        telemetryRef.current.touch?.destroy?.();
        telemetryRef.current.motion?.destroy?.();
        telemetryRef.current = null;
      }
    };
    window.addEventListener("veratad-camera-error", handleCameraErrorEvent);
    return () => window.removeEventListener("veratad-camera-error", handleCameraErrorEvent);
  }, []);

  const captureTerminal = useCallback((result) => {
    if (!result) return true;
    const status = result.status;
    const processState = result.process_state;
    if (processState === "NEXT_APP_TRIGGER" || processState === "COMPLETED" ||
        status === "PASS" || status === "FAIL") {
      trackEvent("verification_done", { status, process_state: processState });
    }
    if (processState === "NEXT_APP_TRIGGER") {
      setTerminalStatus("NEXT_APP");
      return false;
    }
    if (processState === "COMPLETED") {
      setTerminalStatus(status === "PASS" ? "PASS" : "FAIL");
      return false;
    }
    if (status === "PASS") {
      setTerminalStatus("PASS");
      return false;
    }
    if (status === "FAIL") {
      setTerminalStatus("FAIL");
      return false;
    }
    return true;
  }, []);

  const handleProcessingComplete = useCallback(() => {
    if (terminalStatus === "NEXT_APP") {
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "TRIGGER_NAVIGATION" });
      return;
    }
    dispatch({ type: "SET_STATUS", payload: terminalStatus || "FAIL" });
    dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
  }, [terminalStatus, dispatch]);

  // Poll backend for verification status after upload + process trigger
  if (data?.edge === true) {
    useEdgeStatusCheck(isReadyForStatusCheck, captureTerminal);
  } else {
    useStatusCheck(isReadyForStatusCheck, false, "face-capture/veratad", captureTerminal);
  }

  const startCapture = () => {
    console.log("[FaceCapture] startCapture called, showFirefoxPrompt:", showFirefoxPrompt);
    if (showFirefoxPrompt) return;
    setLoading(true);
    setIsCapture(true);
    setUploadFailed(false);
    uploadInFlightRef.current = false;
    console.log("[FaceCapture] isCapture set to true — VeratadCapture will mount");
    sessionTimestampsRef.current.capture_start = Date.now();
    trackEvent("capture_started", { face_ui: "match_v1" });

    // Initialize interaction telemetry trackers
    telemetryRef.current = {
      visibility: createVisibilityTracker(),
      touch: createTouchTracker(),
      motion: createMotionTracker(),
    };

    if (settings?.request_id) {
      requestCaptureChallenge(settings.request_id)
        .then((c) => { challengeRef.current = c; })
        .catch((err) => console.warn("[CaptureIntegrity] Challenge request failed:", err));
    }
  };

  const handleReady = (cameraSignals) => {
    setLoading(false);
    // Store camera signals from the active video track for integrity telemetry
    if (cameraSignals && telemetryRef.current) {
      telemetryRef.current.camera = cameraSignals;
    }
    // If the Android camera block was shown earlier in this tab, the user
    // followed the instructions and camera is now working — fire the
    // recovery event for the funnel. No-op if no marker is present.
    fireAndroidBlockRecoveredIfPending({
      requestId: settings.request_id,
      captureType: "face",
      isBrowserHandoffSession: false,
    });
  };

  // Set when the current upload was driven by the Android camera-app
  // fallback path (selfie taken via system camera app instead of the
  // real-time react-webcam stream). Used to fire the _fallback_uploaded
  // event from handleCapture after processAndUploadWithIntegrity resolves.
  const fallbackUploadPendingRef = useRef(false);

  // True after the user has submitted a file via the camera-app fallback.
  // Used to bypass the force_android_block early return so the loading
  // overlay and processing screen can render normally — without this,
  // force mode would keep showing the portal even after the file was
  // uploaded, which made users think nothing happened and tap again.
  const [fallbackInProgress, setFallbackInProgress] = useState(false);

  // Camera-app fallback: user tapped "Take a selfie with the camera app"
  // on the Android block portal and the system camera app returned a File.
  // Route it through the existing handleCapture upload pipeline — the
  // backend doesn't care that the bytes came from a different code path.
  // No frameChain (we have no real-time frames).
  const handleSelfieFallback = (file) => {
    if (!file) return;
    console.log("[FaceCapture] handleSelfieFallback: file received", file.name, file.size, file.type);
    fallbackUploadPendingRef.current = true;
    setFallbackInProgress(true);
    const previewUrl = URL.createObjectURL(file);
    handleCapture(file, previewUrl, null);
  };

  const handleCapture = async (blob, previewUrl, frameChain) => {
    // One upload + one estimation dispatch per capture attempt. Extra
    // invocations (burst manual-shutter taps, or auto-capture firing
    // after a manual tap) are dropped; the ref resets on failure and
    // on a fresh startCapture so retries still work.
    if (uploadInFlightRef.current) {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch (e) { /* ignore */ }
      }
      return;
    }
    uploadInFlightRef.current = true;
    loadingStartTimeRef.current = Date.now();
    setIsCapture(false);
    setLoading(true);

    // Clean up the preview URL since we only need the blob for upload
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch (e) { /* ignore */ }
    }

    // Defensive: if blob is a string (data URL from a stale cached capture.js),
    // convert it to a proper Blob before uploading. Without this, fetch(blob)
    // could stringify to "[object Blob]" and the server would return HTML.
    if (typeof blob === "string") {
      try {
        const [header, b64] = blob.split(",");
        const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: mime });
      } catch (e) {
        console.error("[FaceCapture] Failed to convert data URL to blob:", e);
        uploadInFlightRef.current = false;
        setLoading(false);
        return;
      }
    }

    try {
      const imageKey = `${settings.request_id}_dl_selfie.jpeg`;
      sessionTimestampsRef.current.capture_end = Date.now();
      trackEvent("upload_started", { bytes: blob?.size });
      const uploadStart = performance.now();
      await processAndUploadWithIntegrity(
        imageKey, blob, challengeRef.current, settings.request_id,
        "selfie", frameChain, sessionTimestampsRef.current, settings,
        telemetryRef.current
      );
      trackEvent("upload_completed", { upload_ms: Math.round(performance.now() - uploadStart) });

      // If this upload was driven by the Android camera-app fallback,
      // fire the _fallback_uploaded event so the funnel can measure
      // end-to-end fallback success for selfie capture too.
      if (fallbackUploadPendingRef.current) {
        fallbackUploadPendingRef.current = false;
        trackEvent("android_camera_os_block_fallback_uploaded", {
          capture_type: "face",
          is_browser_handoff: false,
          file_size_bytes: blob?.size,
          file_type: blob?.type,
        });
      }

      // Clean up telemetry trackers
      if (telemetryRef.current) {
        telemetryRef.current.visibility?.destroy?.();
        telemetryRef.current.touch?.destroy?.();
        telemetryRef.current.motion?.destroy?.();
        telemetryRef.current = null;
      }

      // If used as a child component with onCompletion callback, just call it
      if (typeof onCompletion === "function") {
        setLoading(false);
        onCompletion();
        return;
      }

      // Standalone route: trigger backend processing and poll for status
      setLoading(false);
      setIsProcessing(true);

      // Prepare data for backend processing
      data.request_id = settings.request_id;
      data.type = "SDK_SCAN";
      data.country = settings.country;
      if (!("front_ext" in data)) {
        data.front_ext = "jpeg";
        data.back_ext = "jpeg";
      }

      // POST to process endpoint to trigger verification
      const edgeQueueUrl = getEdgeQueueUrl();

      const endpoint =
        data?.edge === true
          ? edgeQueueUrl
          : process.env.REACT_APP_BACKEND_URL +
            "/v3/stable/api/frontend/estimate/process.php";

      const requestBody =
        data?.edge === true ? { ...data, queue: "estimate" } : data;

      trackEvent("processing_requested");
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      }).catch((err) => console.error("Process request failed:", err));

      // Start status polling after a delay to let the backend begin processing
      const elapsed = Date.now() - loadingStartTimeRef.current;
      const remainingDelay = Math.max(0, 5000 - elapsed);
      setTimeout(() => {
        setIsReadyForStatusCheck(true);
      }, remainingDelay);

      // Send postMessage event
      if (postPayloadMessage) {
        postPayloadMessage({
          event: "VERIFICATION_INITIATED",
          app: "face-capture/veratad",
        });
      }
    } catch (err) {
      console.error("Face capture upload failed:", err);
      trackEvent("upload_failed", { error: err?.message });
      uploadInFlightRef.current = false;
      setUploadFailed(true);
      setLoading(false);
      setIsProcessing(false);
    }
  };

  // Handle all errors from the capture component (camera denial, model load
  // failures, capture errors). Camera errors also show the denial message.
  const handleError = useCallback((err) => {
    console.error("[FaceCapture] handleError called with:", JSON.stringify(err));
    if (!captureFailedFiredRef.current) {
      captureFailedFiredRef.current = true;
      trackEvent("face_capture_failed", {
        provider: "veratad",
        error_type: err?.type || "unknown",
        error_name: err?.name || err?.message || "unknown",
      });
    }
    setLoading(false);
    setIsCapture(false);
    if (err?.type === "camera_error") {
      setCameraError(true);
    }
    if (telemetryRef.current) {
      telemetryRef.current.visibility?.destroy?.();
      telemetryRef.current.touch?.destroy?.();
      telemetryRef.current.motion?.destroy?.();
      telemetryRef.current = null;
    }
  }, []);

  // Force mode: render the Android camera block portal directly, bypassing
  // device detection and the entire capture flow. Used by testers to verify
  // the selfie fallback end-to-end without needing a real Android device
  // with the camera actually blocked.
  //
  //   ?force_android_block=os_toggle         → Quick Settings variant
  //   ?force_android_block=chrome_permission → legacy Chrome perms variant
  //   ?force_android_block=true              → defaults to os_toggle
  const forceAndroidBlock = new URLSearchParams(window.location.search).get("force_android_block");
  // Once the user has submitted a fallback file, we want the loading
  // overlay / processing screen to take over — so we stop blocking the
  // normal render flow with this early return.
  if (
    !fallbackInProgress &&
    (forceAndroidBlock === "os_toggle" ||
      forceAndroidBlock === "chrome_permission" ||
      forceAndroidBlock === "true")
  ) {
    return (
      <AndroidCameraBlockPortal
        locale={locale}
        requestId={settings.request_id}
        captureType="face"
        isBrowserHandoffSession={false}
        androidVariant={
          forceAndroidBlock === "chrome_permission"
            ? "chrome_permission_legacy"
            : "os_toggle_12plus"
        }
        onPhotoFallback={handleSelfieFallback}
        captureMode="user"
      />
    );
  }

  if (showFirefoxPrompt) {
    return (
      <FirefoxCameraWarning onContinue={() => setShowFirefoxPrompt(false)} />
    );
  }

  // Render the staged processing screen only for the S3 upload phase
  // (loading flips true post-capture, when isCapture has just been
  // set false) and the post-upload backend wait (isProcessing).
  // startCapture also sets loading=true with isCapture=true while the
  // camera is initializing — that's a different state and should
  // fall through to the camera UI, not hijack it with the processing
  // screen.
  if ((loading && !isCapture) || isProcessing) {
    // Embedded use (parent supplied onCompletion — i.e. doc-selfie
    // via dcams) gets the single-stage "Uploading your selfie…"
    // variant so it doesn't bleed the face-age stages into a flow
    // that owns its own post-selfie verification stages. Standalone
    // routes still use the full face-age sequence.
    const processingType = onCompletion ? "selfie-upload" : "face-age";
    return (
      <VerificationProcessingScreen
        type={processingType}
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }

  return (
    <>
      {/* Camera-init overlay: loading=true with isCapture=true means
          startCapture just fired and we're waiting for the webcam to
          mount. Keep the lightweight Mantine overlay here — the
          staged processing screen would be misleading at this stage
          since nothing has been captured yet. */}
      {loading && isCapture && <LoadingOverlay visible />}
      {!isCapture ? (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box style={{ overflowY: "auto", minHeight: 0 }}>
            <Stack align="center" spacing={isSmallMobile ? "xs" : "md"}>
              <ThemeIcon
                variant="light"
                c={data?.styles?.primaryColor ?? theme.primaryColor}
                size={isSmallMobile ? 60 : 85}
                radius="xl"
              >
                <IconFaceId size={isSmallMobile ? 45 : 70} stroke={1} />
              </ThemeIcon>
              <Text
                fz={isSmallMobile ? 20 : 30}
                fw={700}
                align="center"
              >
                {locale?.setup?.title || "Face Match to ID Document"}
              </Text>
            </Stack>
            <Text align="center" fz={isSmallMobile ? "sm" : undefined}>
              {locale?.setup?.instructions || "Before you get started ensure the following:"}
            </Text>
            <Paper
              withBorder
              m={isSmallMobile ? "xs" : "md"}
              p={isSmallMobile ? "sm" : "md"}
              style={{
                backgroundColor:
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[6]
                    : "#fafafa",
              }}
            >
              <Stack spacing={isSmallMobile ? "xs" : "xl"}>
                <Group noWrap spacing={isSmallMobile ? "xs" : "sm"}>
                  <IconSunHigh size={isSmallMobile ? 18 : 30} />
                  <Text fz={isSmallMobile ? 13 : 18}>
                    {locale?.setup?.wellLit || "You are in a well-lit area"}
                  </Text>
                </Group>
                <Group noWrap spacing={isSmallMobile ? "xs" : "sm"}>
                  <IconChefHatOff size={isSmallMobile ? 18 : 30} />
                  <Text fz={isSmallMobile ? 13 : 18}>
                    {locale?.setup?.removeHeadwear || "Remove any headwear"}
                  </Text>
                </Group>
                <Group noWrap spacing={isSmallMobile ? "xs" : "sm"}>
                  <IconEyeglass2 size={isSmallMobile ? 18 : 30} />
                  <Text fz={isSmallMobile ? 13 : 18}>
                    {locale?.setup?.keepGlasses || "Keep your glasses on"}
                  </Text>
                </Group>
              </Stack>
            </Paper>
          </Box>
          <Box>
            {cameraError && (
              <Text fz="sm" c="red" fw={600} align="center" mb="sm">
                {locale?.errors?.cameraDenied ||
                  "You have denied camera permissions. Please go into settings and allow camera, refresh the page, and try again."}
              </Text>
            )}
            {uploadFailed && !cameraError && (
              <Text fz="sm" c="red" fw={600} align="center" mb="sm">
                {locale?.error?.message ||
                  "Something went wrong while uploading your image. Please try again."}
              </Text>
            )}
            <Text fz="sm" c="dimmed" align="center">
              {locale?.setup?.cameraPermission || "We will ask you to allow camera permissions"}
            </Text>
            <Button
              size="lg"
              onClick={startCapture}
              fullWidth
              disabled={!modelsReady}
              leftIcon={
                !modelsReady ? (
                  <Loader size="xs" color={data?.styles?.components?.button?.color || "#fff"} />
                ) : undefined
              }
            >
              {modelsReady
                ? locale?.setup?.getStarted || "Get Started"
                : locale?.setup?.preparing || "Getting ready…"}
            </Button>
          </Box>
        </Box>
      ) : (
        <VeratadCapture
          onCapture={handleCapture}
          onError={handleError}
          onReady={handleReady}
          onPhotoFallback={handleSelfieFallback}
          requestId={settings.request_id}
        />
      )}
    </>
  );
}
